/**
 * QC Approve Modal — QC-APPROVE-UI-002 (Oct 2026) compact premium redesign.
 * Business rules unchanged: QC-PORTAL-HANDOFF-001 ack is mandatory (and server-enforced)
 * for External Portal orders whose client skips Typing.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, X, FileText, User } from 'lucide-react';
import { PortalHandoffSection } from './PortalHandoffSection';
import { isSearchProduct } from '../../utils/orderHelpers';
import './qc-approve-modal.css';
import { qcComponentOf, COMPONENT_LABELS } from '../../lib/qcComponent';

const API = process.env.REACT_APP_BACKEND_URL;
const NOTES_MAX = 500;

const SuccessState = ({ label }) => (
  <div className="flex flex-col items-center justify-center py-14 gap-4" data-testid="qc-approve-success">
    <span className="qcam-success-ring grid place-items-center w-20 h-20 rounded-full bg-emerald-100">
      <svg viewBox="0 0 48 48" className="w-11 h-11" fill="none" stroke="#059669" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <path className="qcam-success-path" d="M12 25l8 8 16-18" />
      </svg>
    </span>
    <p className="text-lg font-semibold text-slate-900">{label}</p>
  </div>
);

const QCApproveModal = ({ isOpen, onClose, onConfirm, order }) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [handoff, setHandoff] = useState(null); // QC-PORTAL-HANDOFF-001
  const [portalAck, setPortalAck] = useState(false);
  const isTitle = (order?.product_type_normalized || '').toLowerCase() === 'title' || isSearchProduct(order);

  useEffect(() => {
    if (!isOpen || !order?.id) return;
    setHandoff(null); setPortalAck(false); setSuccess(false);
    if (!isTitle) return;
    axios.get(`${API}/api/orders/${order.id}/qc-documents/portal-handoff`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setHandoff(r.data)).catch(() => setHandoff({ required: false, documents: [] }));
  }, [isOpen, order?.id, isTitle]);

  if (!isOpen || !order) return null;
  // The approver completes the order only when the client skips Typing; Typing-client portal docs are confirmed by the typist.
  const handoffHere = !!(handoff?.required && handoff?.handoff_stage === 'qc_approve');
  const handoffLoading = isTitle && handoff === null;
  const completesHere = !isTitle || handoffHere;
  // MLS-COMPONENT-QC-001: component-level item → approve ONLY that component (order finalizes later, when both are complete)
  const qcComp = qcComponentOf(order);
  const actionLabel = qcComp ? `Approve ${COMPONENT_LABELS[qcComp]} Component` : (completesHere ? 'Approve & Complete' : 'Approve & Send Onward');

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const ok = await onConfirm(notes.trim() || null, handoffHere
        ? { portal_upload_confirmed: portalAck, portal_upload_file_ids: handoff.documents.map(d => d.file_id) }
        : {});
      if (ok) { setSuccess(true); setNotes(''); }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => { if (success) return; setNotes(''); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="qcam-backdrop absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]" onClick={handleClose} />

      <div className="qcam-panel relative w-full max-w-[760px] bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(15,23,42,0.45)] overflow-hidden" role="dialog" aria-modal="true" data-testid="qc-approve-modal">
        {success ? <SuccessState label={completesHere ? 'Approved & Completed' : 'Approved'} /> : (
          <>
            {/* Header */}
            <div className="flex items-center gap-4 px-7 py-5 bg-emerald-50/70 border-b border-emerald-100/80">
              <span className="grid place-items-center w-14 h-14 rounded-full bg-emerald-100 shrink-0">
                <CheckCircle className="w-8 h-8 text-emerald-600" strokeWidth={2.2} />
              </span>
              <div className="flex-1 min-w-0">
                <h2 className="text-[22px] leading-tight font-bold text-slate-900">{qcComp ? `Approve ${COMPONENT_LABELS[qcComp]} Component` : 'Approve QC Review'}</h2>
                <p className="text-[14px] text-slate-500 mt-0.5 truncate">Order {order.order_number}{qcComp ? ` · ${qcComp === 'tax' ? 'Tax Search' : 'MLS Search'} component only — the order completes once both components are approved` : ''}</p>
              </div>
              <button type="button" onClick={handleClose} className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" aria-label="Close" data-testid="qc-approve-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-7 pt-5 pb-6 space-y-5">
              <div className="grid grid-cols-2 rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] divide-x divide-slate-200" data-testid="qc-approve-context">
                <div className="flex items-center gap-3 px-5 py-3.5">
                  <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[12px] text-slate-500">Product</p>
                    <p className="text-[15px] font-semibold text-slate-900 truncate">{order.client_product || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-5 py-3.5">
                  <User className="w-5 h-5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[12px] text-slate-500">Submitted by</p>
                    <p className="text-[15px] font-semibold text-slate-900 truncate">{order.qc_submitted_by_name || 'Unknown'}</p>
                  </div>
                </div>
              </div>

              {handoffHere && (
                <PortalHandoffSection orderId={order.id} documents={handoff.documents} checked={portalAck} onChange={setPortalAck} disabled={isSubmitting} />
              )}

              <div>
                <label htmlFor="qc-approve-notes" className="block text-[14px] font-semibold text-slate-800 mb-1.5">
                  Internal Notes <span className="font-normal text-slate-500">(Optional)</span>
                </label>
                <textarea
                  id="qc-approve-notes"
                  value={notes}
                  maxLength={NOTES_MAX}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any internal notes about this approval..."
                  className="qcam-notes w-full px-3.5 py-2.5 text-[14px] border border-slate-200 rounded-xl bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500 resize-y"
                  rows={1}
                  data-expanded={notes.length > 0}
                  data-testid="qc-approve-notes"
                />
                <p className="text-right text-[12px] text-slate-400 mt-1" data-testid="qc-approve-notes-count">{notes.length}/{NOTES_MAX}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-7 py-4 border-t border-slate-200 bg-slate-50/70">
              <button type="button" onClick={handleClose} disabled={isSubmitting}
                className="h-11 px-6 rounded-xl border border-slate-300 bg-white text-[15px] font-semibold text-slate-800 hover:bg-slate-50 transition-colors disabled:opacity-60"
                data-testid="qc-approve-cancel">
                Cancel
              </button>
              <button type="button" onClick={handleConfirm}
                disabled={isSubmitting || handoffLoading || (handoffHere && !portalAck)}
                className="qcam-primary inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[15px] font-semibold"
                data-testid="qc-approve-confirm">
                {isSubmitting ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Approving...</>
                ) : (
                  <><CheckCircle className="w-5 h-5" /> {actionLabel}</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QCApproveModal;
